import React, { useState, useEffect } from 'react';
import { Upload, Image, Button, Modal, Space, message, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import axios from 'axios';

const PhotoGallery = ({ value = [], onChange }) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [loading, setLoading] = useState(false);


  // Инициализация значения - только если value не определено
  useEffect(() => {
    if (value === undefined) {
      onChange && onChange([]);
    }
  }, [value, onChange]);

  const handleUpload = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await axios.post('https://limitverify.ru:5000/api/upload/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const newPhoto = {
        id: Date.now(), // временный ID
        photoUrl: response.data.photoUrl,
        displayOrder: (value?.length || 0) + 1,
        isPrimary: (value?.length || 0) === 0 // первое фото - основное
      };

      const updatedPhotos = [...(value || []), newPhoto];
      onChange && onChange(updatedPhotos);
      message.success('Фото загружено успешно!');
      
    } catch (error) {
      console.error('Ошибка загрузки фото:', error);
      message.error('Ошибка загрузки фото');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (photoId) => {
    const updatedPhotos = (value || []).filter(photo => photo.id !== photoId);
    
    // Если удаляем основное фото, делаем первое оставшееся основным
    const removedPhoto = (value || []).find(photo => photo.id === photoId);
    if (removedPhoto && removedPhoto.isPrimary && updatedPhotos.length > 0) {
      updatedPhotos[0].isPrimary = true;
    }
    
    onChange && onChange(updatedPhotos);
    message.success('Фото удалено');
  };

  const handleSetPrimary = (photoId) => {
    const updatedPhotos = (value || []).map(photo => ({
      ...photo,
      isPrimary: photo.id === photoId
    }));
    onChange && onChange(updatedPhotos);
    message.success('Основное фото изменено');
  };


  const handlePreview = (photoUrl) => {
    setPreviewImage(photoUrl);
    setPreviewVisible(true);
  };

  const uploadButton = (
    <div style={{ 
      width: 104, 
      height: 104, 
      border: '1px dashed #d9d9d9',
      borderRadius: 6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      backgroundColor: '#fafafa'
    }}>
      <PlusOutlined style={{ fontSize: '20px', color: '#999' }} />
      <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
        Загрузить
      </div>
    </div>
  );

  // Сортируем фотографии по ID (порядок загрузки/создания)
  const sortedPhotos = (value || []).sort((a, b) => {
    // Сортируем по ID в порядке возрастания (старые фото первыми)
    if (typeof a.id === 'number' && typeof b.id === 'number') {
      return a.id - b.id;
    }
    // Если ID строковые, конвертируем в числа
    if (typeof a.id === 'string' && typeof b.id === 'string') {
      const aNum = parseInt(a.id, 10);
      const bNum = parseInt(b.id, 10);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
    }
    // Если есть displayOrder, используем его
    if (a.displayOrder && b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    return 0;
  });

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {sortedPhotos.map((photo, index) => (
          <div key={photo.id || index} style={{ position: 'relative' }}>
            <div style={{
              width: 104,
              height: 104,
              border: photo.isPrimary ? '2px solid #1890ff' : '1px solid #d9d9d9',
              borderRadius: 6,
              overflow: 'hidden',
              position: 'relative'
            }}>
              <Image
                src={photo.photoUrl}
                alt={`Фото ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                preview={false}
              />
              
              {/* Индикатор основного фото */}
              {photo.isPrimary && (
                <div style={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  backgroundColor: '#1890ff',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px'
                }}>
                  ★
                </div>
              )}
              
              {/* Кнопки управления */}
              <div style={{
                position: 'absolute',
                top: 4,
                right: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}>
                <Tooltip title="Просмотр">
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(photo.photoUrl)}
                    style={{ 
                      backgroundColor: 'rgba(0,0,0,0.5)', 
                      color: 'white',
                      border: 'none',
                      width: 24,
                      height: 24,
                      minWidth: 24
                    }}
                  />
                </Tooltip>
                <Tooltip title={photo.isPrimary ? "Основное фото" : "Сделать основным"}>
                  <Button
                    type="text"
                    size="small"
                    icon={photo.isPrimary ? <StarFilled /> : <StarOutlined />}
                    onClick={() => handleSetPrimary(photo.id)}
                    style={{ 
                      backgroundColor: 'rgba(0,0,0,0.5)', 
                      color: photo.isPrimary ? '#ffd700' : 'white',
                      border: 'none',
                      width: 24,
                      height: 24,
                      minWidth: 24
                    }}
                  />
                </Tooltip>
                <Tooltip title="Удалить">
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(photo.id)}
                    style={{ 
                      backgroundColor: 'rgba(255,0,0,0.7)', 
                      color: 'white',
                      border: 'none',
                      width: 24,
                      height: 24,
                      minWidth: 24
                    }}
                  />
                </Tooltip>
              </div>
            </div>
            
            {/* Номер фото */}
            <div style={{
              position: 'absolute',
              bottom: -20,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '10px',
              color: '#666'
            }}>
              {index + 1}
            </div>
          </div>
        ))}
        
        {/* Кнопка загрузки */}
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={loading}
        >
          {uploadButton}
        </Upload>
      </div>
      
      {/* Модальное окно предпросмотра */}
      <Modal
        open={previewVisible}
        title="Предпросмотр фото"
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Закрыть
          </Button>
        ]}
        onCancel={() => setPreviewVisible(false)}
      >
        <Image
          src={previewImage}
          style={{ width: '100%' }}
          alt="Предпросмотр"
        />
      </Modal>
      
      {/* Информация */}
      {(value || []).length > 0 && (
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
          Загружено фото: {(value || []).length}
          <span style={{ marginLeft: '12px', color: '#999' }}>
            (отсортированы по порядку загрузки)
          </span>
          {(value || []).find(p => p.isPrimary) && (
            <span style={{ marginLeft: '12px', color: '#1890ff' }}>
              ★ Основное фото отмечено
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
